import { DatabaseIcon, LockIcon, PencilIcon, UsersIcon } from "lucide-react";
import Link from "next/link";
import {
  Fragment,
  type ComponentType,
  type MouseEventHandler,
  type ReactNode,
} from "react";

import {
  ActorHoverView,
  type ActorIdentity,
} from "@/components/app/entities/actor-hover-view";
import type { ActorDisplay } from "@/components/app/feed/feed-actor";
import { FeedDayHeader } from "@/components/app/feed/feed-day-groups";
import { ActorMark } from "@/components/app/runs/provenance-avatar-stack";
import { Button } from "@/components/ui/button";
import {
  PlainRow,
  PlainTable,
  PlainTableHead,
  PlainTd,
  PlainTh,
  RelativeTime,
} from "@/components/app/workspace/plain-table";
import { channelVisibilityLabel } from "@/lib/channel-visibility";
import { formatRelativeTime } from "@/lib/format";
import { channelDataSetHref, channelHref } from "./channel-hrefs";
import { ChannelTabColumn } from "./channel-tab-column";

/*
 * The channel Overview as markup. ChannelHome and its sections keep the
 * queries, editing, the thread sheet and pane navigation, and render the tab
 * through these, so a fixture channel composes the same Overview: purpose,
 * who is in it and what it keeps, the ask box, its data sets and what changed.
 */

/** A channel link's click handler, where a session pane opens channel links in place. */
type PaneLink = (href: string) => MouseEventHandler<HTMLAnchorElement> | undefined;

/** What opens an actor's card from their avatar; closed, it renders as ActorHoverView. */
type ActorHoverComponent = ComponentType<{
  actor: ActorIdentity;
  className?: string;
  children: ReactNode;
}>;

/** The Overview tab: what the channel is for, then its set-up steps, data sets and recent activity. */
export function ChannelHomeView({
  purpose,
  meta,
  ask,
  setup,
  dataSets,
  activity,
}: {
  purpose: ReactNode;
  meta: ReactNode;
  /** The ask box, on the full channel page. */
  ask?: ReactNode;
  /** A new channel's set-up steps. */
  setup?: ReactNode;
  /** The data set table, or the empty state that offers the first one. */
  dataSets?: ReactNode;
  activity: ReactNode;
}) {
  return (
    // Laid out by its own width, so it also fits the narrower session pane.
    <ChannelTabColumn scrollClassName="@container" className="flex flex-col gap-7">
      <section aria-label="About this channel" className="flex flex-col gap-3">
        {purpose}
        {meta}
        {/* The page's first step in every state: a new channel too starts with a question. */}
        {ask}
      </section>
      {setup}
      {dataSets ? (
        // The Data and Feed tabs sit just above, so sections need no titles.
        <section aria-label="Data sets">{dataSets}</section>
      ) : null}
      {activity}
    </ChannelTabColumn>
  );
}

/** The channel's purpose as its members read it, or a prompt to add one. */
export function ChannelPurposeView({
  purpose,
  action,
  editor,
}: {
  purpose: string;
  /** Edit or Add purpose, for those who can manage the channel. */
  action?: ReactNode;
  /** The purpose's editor, which opens from the action. */
  editor?: ReactNode;
}) {
  const trimmed = purpose.trim();
  return (
    <section aria-label="Purpose" className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        {trimmed ? (
          <p className="text-[15px] leading-6">{trimmed}</p>
        ) : (
          <p className="text-[15px] leading-6 text-muted-foreground">
            No purpose yet. A sentence on what belongs here helps your team and
            your Twin use this channel well.
          </p>
        )}
      </div>
      {action}
      {editor}
    </section>
  );
}

/** Edit, or Add purpose when there is none, for those who can manage the channel. */
export function ChannelPurposeEditButton({
  hasPurpose,
  onClick,
}: {
  hasPurpose: boolean;
  onClick?: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="shrink-0"
      onClick={onClick}
    >
      <PencilIcon data-icon="inline-start" />
      {hasPurpose ? "Edit" : "Add purpose"}
    </Button>
  );
}

/** Who can see the channel, who is in it, what it keeps and when it last changed. */
export function ChannelMetaView({
  channelId,
  visibility,
  updatedAt,
  memberCount,
  members = [],
  fileCount,
  memoryCount,
  paneLink,
  actorHover: Hover = ActorHoverView,
}: {
  channelId: string;
  visibility: "private" | "shared" | undefined;
  updatedAt: number;
  /** How many people are in the channel; unknown or none hides them. */
  memberCount: number | null;
  /** Up to four of them to show, as the directory names them. */
  members?: Pick<ActorDisplay, "name" | "imageUrl" | "initials" | "identity">[];
  fileCount: number | null;
  memoryCount: number | null;
  paneLink?: PaneLink;
  actorHover?: ActorHoverComponent;
}) {
  const VisibilityIcon = visibility === "private" ? LockIcon : UsersIcon;
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <VisibilityIcon aria-hidden className="size-3.5" />
        {channelVisibilityLabel(visibility)}
      </span>
      {memberCount ? (
        <>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1.5">
            {members.length > 0 ? (
              <span className="flex -space-x-1.5">
              {members.map((member, index) => {
                const mark = (
                  <ActorMark
                    actor={{ label: member.name, imageUrl: member.imageUrl, initials: member.initials }}
                    className="size-5"
                  />
                );
                return member.identity ? (
                  <Hover key={`${member.name}:${index}`} actor={member.identity} className="inline-flex rounded-full">
                    {mark}
                  </Hover>
                ) : (
                  <span key={`${member.name}:${index}`} className="inline-flex">{mark}</span>
                );
              })}
              </span>
            ) : null}
            {memberCount === 1 ? "1 member" : `${memberCount} members`}
          </span>
        </>
      ) : null}
      {fileCount ? (
        <>
          <span aria-hidden>·</span>
          <MetaLink href={channelHref(channelId, "files")} paneLink={paneLink}>
            {fileCount === 1 ? "1 file" : `${fileCount} files`}
          </MetaLink>
        </>
      ) : null}
      {memoryCount ? (
        <>
          <span aria-hidden>·</span>
          <MetaLink href={channelHref(channelId, "memory")} paneLink={paneLink}>
            {memoryCount === 1 ? "1 memory" : `${memoryCount} memories`}
          </MetaLink>
        </>
      ) : null}
      <span aria-hidden>·</span>
      <span>Updated {formatRelativeTime(updatedAt)}</span>
    </p>
  );
}

function MetaLink({
  href,
  paneLink,
  children,
}: {
  href: string;
  paneLink?: PaneLink;
  children: string;
}) {
  return (
    <Link
      href={href}
      onClick={paneLink?.(href)}
      className="hover:text-foreground hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
    </Link>
  );
}

/** A data set in the Overview's table: what it is, when it changed and its views. */
export type ChannelDataSetTableGroup = {
  id: string;
  name: string;
  description: string;
  updatedAt: number | null;
  views: { id: string; name: string }[];
};

/** The channel's data sets in the plain table recipe: open one, or one of its views. */
export function ChannelDataSetTableView({
  channelId,
  groups,
  defaultViewId,
  paneLink,
}: {
  channelId: string;
  groups: ChannelDataSetTableGroup[];
  /** The channel's default view, which a data set opens in when it has it. */
  defaultViewId: string | null;
  paneLink?: PaneLink;
}) {
  return (
    <PlainTable striped>
      <PlainTableHead>
        <PlainTh className="px-2 @xl:w-[24%]">Data set</PlainTh>
        <PlainTh className="hidden @2xl:table-cell">Description</PlainTh>
        <PlainTh className="hidden w-[26%] @xl:table-cell">Views</PlainTh>
        <PlainTh className="w-28">Updated</PlainTh>
      </PlainTableHead>
      <tbody>
        {groups.map((group) => {
          const view =
            group.views.find((candidate) => candidate.id === defaultViewId) ??
            group.views[0];
          const views = group.views.slice(0, 2);
          const href = channelDataSetHref(channelId, group.id, view?.id);
          return (
            <PlainRow key={group.id}>
              <PlainTd className="px-2">
                <Link
                  href={href}
                  onClick={paneLink?.(href)}
                  className="inline-flex max-w-full items-center gap-2 underline-offset-2 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <DatabaseIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{group.name}</span>
                </Link>
              </PlainTd>
              <PlainTd className="hidden text-muted-foreground @2xl:table-cell" title={group.description || undefined}>
                {group.description || "—"}
              </PlainTd>
              <PlainTd className="hidden text-muted-foreground @xl:table-cell">
                {views.map((item, index) => {
                  const viewHref = channelDataSetHref(channelId, group.id, item.id);
                  return (
                    <span key={item.id}>
                      {index > 0 ? ", " : null}
                      <Link
                        href={viewHref}
                        onClick={paneLink?.(viewHref)}
                        className="hover:text-foreground hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {item.name}
                      </Link>
                    </span>
                  );
                })}
                {group.views.length > views.length ? ` +${group.views.length - views.length}` : null}
              </PlainTd>
              <PlainTd className="text-muted-foreground">
                <RelativeTime value={group.updatedAt} />
              </PlainTd>
            </PlainRow>
          );
        })}
      </tbody>
    </PlainTable>
  );
}

/** A day of the channel's recent activity, each row already rendered. */
export type ChannelHomeActivityDay = {
  id: string;
  label: string;
  items: { key: string; row: ReactNode }[];
};

/** What changed in the channel lately, by day, or why nothing shows. */
export function ChannelHomeActivityView({
  days,
  error = false,
  loading = false,
  wrapDays = (content) => content,
  thread,
}: {
  days: ChannelHomeActivityDay[];
  /** The feed could not be read. */
  error?: boolean;
  /** Nothing to show yet because the feed is still loading. */
  loading?: boolean;
  /** Wraps the days; ChannelHomeActivity sets compact rows there. */
  wrapDays?: (content: ReactNode) => ReactNode;
  /** After the days: the thread sheet in the application. */
  thread?: ReactNode;
}) {
  return (
    // Each day's label heads its rows; the Feed tab holds the rest.
    <section aria-label="Recent activity" className="-mt-4 flex flex-col">
      {error ? (
        <p className="pt-4 text-sm text-muted-foreground">Recent activity could not be loaded.</p>
      ) : days.length === 0 ? (
        <p className="pt-4 text-sm text-muted-foreground">
          {loading ? "Loading activity…" : "Posts, sessions and changes in this channel will show here."}
        </p>
      ) : (
        wrapDays(
          days.map((day) => (
            <div key={day.id} className="flex flex-col">
              <FeedDayHeader label={day.label} />
              {/* Each row is its own list item. */}
              <ol className="flex flex-col">
                {day.items.map((item) => (
                  <Fragment key={item.key}>{item.row}</Fragment>
                ))}
              </ol>
            </div>
          )),
        )
      )}
      {thread}
    </section>
  );
}
